/**
 * Cost Dashboard Shared Components
 * 
 * Reusable UI components for the Cost Category dashboard.
 */

import { cn } from '@/lib/utils';
import { ArrowLeft, FileText, LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type CostMeasure } from '@/lib/cost-calculator-utils';

// ============================================================================
// METRIC CARD
// ============================================================================

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: LucideIcon;
  color?: 'primary' | 'emerald' | 'violet' | 'amber' | 'destructive' | 'slate';
  className?: string;
}

const colorClasses = {
  primary: 'bg-primary/10 text-primary',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  destructive: 'bg-destructive/10 text-destructive',
  slate: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
};

export function MetricCard({ title, value, subtitle, icon: Icon, color = 'primary', className }: MetricCardProps) {
  return (
    <div className={cn(
      'bg-card border border-border rounded-xl p-4 flex flex-col',
      className
    )}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-muted-foreground">{title}</p>
        {Icon && (
          <div className={cn('p-2 rounded-lg', colorClasses[color])}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
      )}
    </div>
  );
}

// ============================================================================
// DECILE BAR / GAUGE
// ============================================================================

interface DecileBarProps {
  decile: number;
  showLabels?: boolean;
  showValue?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Decile colors from worst to best (lower cost = higher decile = better)
const decileColors = [
  'bg-red-500',      // 1 - worst (highest cost)
  'bg-red-400',      // 2
  'bg-orange-500',   // 3
  'bg-orange-400',   // 4
  'bg-yellow-500',   // 5
  'bg-yellow-400',   // 6
  'bg-lime-500',     // 7
  'bg-green-400',    // 8
  'bg-green-500',    // 9
  'bg-emerald-500',  // 10 - best (lowest cost)
];

export function DecileBar({ decile, showLabels = false, showValue = false, size = 'md', className }: DecileBarProps) {
  const heights = { sm: 'h-1', md: 'h-2', lg: 'h-3' };
  const gaps = { sm: 'gap-0.5', md: 'gap-0.5', lg: 'gap-1' };
  
  const validDecile = Math.min(10, Math.max(1, Math.round(decile))) || 1;
  
  return (
    <TooltipProvider>
      <div className={cn('flex flex-col', className)}>
        {showLabels && (
          <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
            <span>High Cost</span>
            <span>Low Cost</span>
          </div>
        )}
        <div className={cn('flex items-center', gaps[size])}>
          {Array.from({ length: 10 }, (_, i) => {
            const segmentDecile = i + 1;
            const isFilled = segmentDecile <= validDecile;
            const isActive = segmentDecile === validDecile;
            
            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'flex-1 rounded-sm transition-all',
                      heights[size],
                      isFilled ? decileColors[i] : 'bg-muted',
                      isActive && 'ring-2 ring-offset-1 ring-foreground/30',
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Decile {segmentDecile}: {i < 3 ? 'High' : i < 6 ? 'Medium' : 'Low'} Cost</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        {showValue && (
          <p className="text-xs text-muted-foreground text-center mt-1">
            Decile {validDecile}
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}

// ============================================================================
// CIRCULAR DECILE GAUGE
// ============================================================================

interface DecileGaugeProps {
  decile: number;
  score?: number;
  size?: number;
  showLabel?: boolean;
}

export function DecileGauge({ decile, score, size = 80, showLabel = true }: DecileGaugeProps) {
  const validDecile = Math.min(10, Math.max(1, Math.round(decile))) || 1;
  const percentage = (validDecile / 10) * 100;
  const strokeWidth = size / 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Color based on decile
  const strokeColor = validDecile >= 8 ? '#10b981' : // emerald
                      validDecile >= 6 ? '#84cc16' : // lime
                      validDecile >= 4 ? '#f59e0b' : // amber
                      '#ef4444'; // red

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-muted"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold">{score !== undefined ? score.toFixed(1) : validDecile}</span>
          {showLabel && <span className="text-[10px] text-muted-foreground">D{validDecile}</span>}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DRIVER TYPE LEGEND & BADGE
// ============================================================================

export type DriverType = 'STRENGTH' | 'COST' | 'UTILIZATION' | 'BOTH' | 'LOW_VOLUME';

interface DriverTypeLegendProps {
  className?: string;
}

export function DriverTypeLegend({ className }: DriverTypeLegendProps) {
  const items = [
    { type: 'STRENGTH', label: 'At/below national', description: 'No meaningful excess vs national', color: 'bg-emerald-500' },
    { type: 'COST', label: 'More per episode', description: 'Episodes accumulate more of this service', color: 'bg-amber-500' },
    { type: 'UTILIZATION', label: 'In more episodes', description: 'Service appears in more of your episodes', color: 'bg-sky-500' },
    { type: 'BOTH', label: 'Both', description: 'More per episode AND in more episodes', color: 'bg-red-500' },
  ];

  return (
    <div className={cn('flex flex-wrap gap-4', className)}>
      {items.map(item => (
        <div key={item.type} className="flex items-center gap-2">
          <div className={cn('w-3 h-3 rounded-full', item.color)} />
          <div className="text-xs">
            <span className="font-medium">{item.label}</span>
            <span className="text-muted-foreground ml-1">- {item.description}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

interface DriverBadgeProps {
  type: DriverType;
  size?: 'sm' | 'md';
}

// "More per episode" deliberately replaces "Cost Driver": Medicare rates are
// standardized, so above-national spend per episode reflects service volume/
// level within episodes (intensity), never price — "cost" reads as a price claim.
const driverColors: Record<DriverType, string> = {
  STRENGTH: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  COST: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  UTILIZATION: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  BOTH: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  LOW_VOLUME: 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400',
};

const driverLabels: Record<DriverType, string> = {
  STRENGTH: 'At/below national',
  COST: 'More per episode',
  UTILIZATION: 'In more episodes',
  BOTH: 'Both',
  LOW_VOLUME: 'Low volume',
};

export function DriverBadge({ type, size = 'sm' }: DriverBadgeProps) {
  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 font-medium',
      size === 'sm' ? 'text-[10px]' : 'text-xs',
      driverColors[type]
    )}>
      {driverLabels[type]}
    </span>
  );
}

// ============================================================================
// SCORE BADGE
// ============================================================================

interface ScoreBadgeProps {
  score: number;
  outOf?: number;
  size?: 'sm' | 'md' | 'lg';
}

export function ScoreBadge({ score, outOf = 10, size = 'md' }: ScoreBadgeProps) {
  const percentage = (score / outOf) * 100;
  
  let colorClass = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  if (percentage >= 80) {
    colorClass = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  } else if (percentage >= 60) {
    colorClass = 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400';
  } else if (percentage >= 40) {
    colorClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }

  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <span className={cn('inline-flex items-center rounded-full font-semibold', sizes[size], colorClass)}>
      {score.toFixed(2)}/{outOf}
    </span>
  );
}

// ============================================================================
// TREND INDICATOR
// ============================================================================

interface TrendIndicatorProps {
  direction: 'up' | 'down' | 'neutral';
  value?: string;
  positive?: boolean; // is "up" good or bad? For cost, down is good
  size?: 'sm' | 'md';
}

export function TrendIndicator({ direction, value, positive = false, size = 'sm' }: TrendIndicatorProps) {
  const isGood = (direction === 'down' && !positive) || (direction === 'up' && positive);
  
  const colorClass = direction === 'neutral' 
    ? 'text-muted-foreground' 
    : isGood 
      ? 'text-emerald-600 dark:text-emerald-400' 
      : 'text-red-600 dark:text-red-400';

  const arrows = { up: '↑', down: '↓', neutral: '→' };
  const sizes = { sm: 'text-xs', md: 'text-sm' };

  return (
    <span className={cn('inline-flex items-center gap-1 font-medium', sizes[size], colorClass)}>
      {arrows[direction]}
      {value && <span>{value}</span>}
    </span>
  );
}

// ============================================================================
// EMPTY STATE
// ============================================================================

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export function EmptyState({ icon: Icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Icon className="h-12 w-12 text-muted-foreground/30 mb-4" />
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      {description && (
        <p className="text-muted-foreground text-sm max-w-md mb-4">{description}</p>
      )}
      {action}
    </div>
  );
}

// ============================================================================
// SCORE-SUBMISSION-ONLY MEASURE DETAIL
// ============================================================================

function formatCurrencyCompact(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

interface ScoreSubmissionOnlyViewProps {
  measure: CostMeasure;
  onClose: () => void;
}

/**
 * Detail view when a cost measure has a CMS score submission but no patient-level
 * feedback files (scoreSource === 'cms_submission_only'). Keeps the score metrics
 * visible and explains why drill-downs are empty.
 */
export function ScoreSubmissionOnlyView({ measure, onClose }: ScoreSubmissionOnlyViewProps) {
  const unitLabel = measure.unitLabel === 'beneficiaries' ? 'Beneficiaries' : 'Episodes';

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose} className="mb-2 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Measures
        </Button>
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">{measure.name}</h2>
          <Badge variant="secondary">CMS Score Submission</Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Score from your CMS score submission file
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Decile Score</p>
          <span className="text-3xl font-bold">{measure.decileScore?.toFixed(1) ?? '—'}</span>
          <div className="mt-2">
            <DecileBar decile={measure.points ?? measure.decileScore ?? 1} />
          </div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{unitLabel}</p>
          <span className="text-3xl font-bold">{(measure.episodes || 0).toLocaleString()}</span>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Your Avg Cost</p>
          <span className="text-3xl font-bold">
            {formatCurrencyCompact(measure.practiceAvgCost || measure.avgCost || 0)}
          </span>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Benchmark</p>
          <span className="text-3xl font-bold">
            {formatCurrencyCompact(measure.nationalMedian || measure.nationalAvg || 0)}
          </span>
          <p className="text-xs text-muted-foreground mt-1">7.5 decile threshold</p>
        </div>
      </div>

      <div className="bg-card border rounded-lg">
        <EmptyState
          icon={FileText}
          title="No patient-level feedback data"
          description="This measure is scored from your CMS score submission — no patient-level cost feedback file was uploaded for it. Upload the matching cost feedback report to see category breakdowns, patients, and episode detail."
        />
      </div>
    </div>
  );
}

// ============================================================================
// PROGRESS BAR
// ============================================================================

interface ProgressBarProps {
  value: number;
  max?: number;
  showValue?: boolean;
  color?: 'primary' | 'emerald' | 'amber' | 'red';
  size?: 'sm' | 'md';
  className?: string;
}

const progressColors = {
  primary: 'bg-primary',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

export function ProgressBar({ 
  value, 
  max = 100, 
  showValue = false, 
  color = 'primary',
  size = 'md',
  className 
}: ProgressBarProps) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const heights = { sm: 'h-1.5', md: 'h-2.5' };
  
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('flex-1 bg-muted rounded-full overflow-hidden', heights[size])}>
        <div 
          className={cn('h-full rounded-full transition-all', progressColors[color])}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showValue && (
        <span className="text-xs text-muted-foreground w-12 text-right">
          {percentage.toFixed(0)}%
        </span>
      )}
    </div>
  );
}










